import { MedicationModel } from "../../../services/api/types/medications/models/MedicationModel";
import { PaginationMeta } from "../../../services/api/types/common/responses/PaginatedResponse";
import { ResidentStatusEnum } from "../../../services/api/types/residents/enums/ResidentStatusEnum";
import { createContext, useContext } from "react";
import { useMedicationListingWithOffline } from "../../offline/hooks/useMedicationListingWithOffline";
import { useAdministerResidentInfo } from "../hooks/useAdministerResidentInfo";

interface Props {
  children: React.ReactNode;
  onFinishedBulkAdminister?: () => void;
  isPaginated?: boolean;
}

interface ResidentMedicationsContextType {
  data: MedicationModel[] | undefined;
  meta: PaginationMeta | undefined;
  isFetching: boolean;
  handlePageChange: (page: number) => void;
  residentInfo: {
    first_name?: string;
    last_name?: string;
    name: string;
    nickname: string | null;
    roomNumber: string;
    bedNumber: string;
    admittedDate: string;
    status: ResidentStatusEnum | undefined;
    profile: string | null;
  };
  frequencies: string[];
  handleFrequencyChange: (frequency: string[]) => void;
  loadFrequencyFilters: () => void;
}

const ResidentMedicationsContext =
  createContext<ResidentMedicationsContextType>(
    {} as ResidentMedicationsContextType,
  );

export function ResidentMedicationsProvider({
  children,
  onFinishedBulkAdminister,
  isPaginated = true,
}: Props) {
  const {
    data,
    meta,
    handlePageChange,
    isFetching,
    frequencies,
    handleFrequencyChange,
    loadFrequencyFilters,
  } = useMedicationListingWithOffline(isPaginated);
  const { residentInfo } = useAdministerResidentInfo();

  const value: ResidentMedicationsContextType = {
    data,
    meta,
    handlePageChange,
    isFetching,
    residentInfo,
    frequencies,
    handleFrequencyChange,
    loadFrequencyFilters,
  };

  return (
    <ResidentMedicationsContext.Provider value={value}>
      {children}
    </ResidentMedicationsContext.Provider>
  );
}

export function useResidentMedicationsContext() {
  const context = useContext(ResidentMedicationsContext);
  if (!context) {
    throw new Error(
      "useResidentMedicationsContext must be used within ResidentMedicationsProvider",
    );
  }
  return context;
}
